"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { categoryLabel } from "@/lib/analytics";
import { formatMoney } from "@/lib/format";
import type { StartupRecord } from "@/lib/types";

const PREVIEW_CATEGORY = "saas";

type ApiResponse = {
  data: StartupRecord[];
  category?: string;
};

export function LandingPreview() {
  const [startups, setStartups] = useState<StartupRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(
          `/api/startups?category=${encodeURIComponent(PREVIEW_CATEGORY)}`,
          { cache: "no-store" }
        );
        if (!res.ok) return;
        const payload = (await res.json()) as ApiResponse;
        const sorted = (payload.data ?? [])
          .sort((a: StartupRecord, b: StartupRecord) => b.mrr - a.mrr)
          .slice(0, 5);
        setStartups(sorted);
      } catch {
        // silent fail for preview
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  return (
    <section id="preview" className="landing-preview">
      <p className="section-label">Live TrustMRR Snapshot</p>
      <h2 className="landing-section-title">
        See the startups behind the market
      </h2>
      <p className="landing-preview-category">
        Showing {categoryLabel(PREVIEW_CATEGORY)} category · Data from TrustMRR
      </p>

      <div className="preview-table-wrap">
        {loading ? (
          <div className="preview-loading">
            <div className="spinner" />
            <span className="loading-text">Loading live data…</span>
          </div>
        ) : startups.length === 0 ? (
          <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 32 }}>
            Unable to load preview data.
          </p>
        ) : (
          <table className="niche-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Startup</th>
                <th>MRR</th>
                <th>Revenue (30d)</th>
              </tr>
            </thead>
            <tbody>
              {startups.map((s, i) => (
                <tr key={s.slug}>
                  <td>
                    <span className={`rank-badge ${i < 3 ? "rank-medal" : "rank-num"}`}>
                      {i + 1}
                    </span>
                  </td>
                  <td className="cell-name">
                    <a
                      href={s.trustmrr_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="startup-link startup-cell"
                    >
                      {s.icon && (
                        <img
                          src={s.icon}
                          alt=""
                          className="startup-logo"
                          width={24}
                          height={24}
                        />
                      )}
                      <span>{s.name}</span>
                    </a>
                  </td>
                  <td className="cell-money">{formatMoney(s.mrr)}</td>
                  <td className="cell-num">{formatMoney(s.revenue_30d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="preview-fade" />
      </div>

      <div className="preview-cta">
        <Link href="/dashboard" className="btn btn--primary">
          Analyze All Niches &rarr;
        </Link>
      </div>
    </section>
  );
}
